import {
  Box,
  MenuItem,
  Select,
  type SelectProps,
  Typography,
  FormHelperText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BOTTOM_MENU_HEIGHT } from "Components/Layout/Menu/BottomMenu";
import { Account } from "Components/Shared/Account/Account";
import {
  type BankAccountListItem,
  BankAccountStatus,
} from "Infrastructure/Api/Api";
import { useRef, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const SPACING_FROM_MENU = 12;
const MIN_MENU_HEIGHT = 150;

type Props = {
  accounts: Array<BankAccountListItem>;
  error?: boolean;
  helperText?: string;
  isBalanceVisible?: boolean;
  isPrimaryChipHidden?: boolean;
  emptyValueText?: string;
} & SelectProps;

export const BankAccountPublicIDSelect = ({
  value = "",
  onChange,
  accounts,
  error,
  helperText,
  isBalanceVisible,
  isPrimaryChipHidden,
  emptyValueText,
  ...restProps
}: Props) => {
  const [pxUntilBottomOfScreen, setPxUntilBottomOfScreen] = useState("");
  const [pxUntilTopOfScreen, setPxUntilTopOfScreen] = useState("");
  const [shouldOpenToTop, setShouldOpenToTop] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  const { t } = useResource();

  const determineMenuPosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const distanceToBottom = window.innerHeight - rect.bottom;
      const distanceToTop = rect.top;
      const distanceToBottomPx = `${distanceToBottom}px`;
      const distanceToTopPx = `${distanceToTop}px`;
      setPxUntilBottomOfScreen(distanceToBottomPx);
      setPxUntilTopOfScreen(distanceToTopPx);
      setShouldOpenToTop(distanceToBottom < MIN_MENU_HEIGHT);
    }
  };

  const maxHeight = shouldOpenToTop
    ? `calc(${pxUntilTopOfScreen} - ${SPACING_FROM_MENU}px)`
    : `calc(${pxUntilBottomOfScreen} - ${BOTTOM_MENU_HEIGHT} - ${SPACING_FROM_MENU}px)`;

  const anchorOrigin = shouldOpenToTop
    ? { vertical: "top" as const, horizontal: "left" as const }
    : { vertical: "bottom" as const, horizontal: "left" as const };

  const transformOrigin = shouldOpenToTop
    ? { vertical: "bottom" as const, horizontal: "left" as const }
    : { vertical: "top" as const, horizontal: "left" as const };

  const canResolveValue =
    !!value && accounts.some(({ publicID }) => publicID === value);

  return (
    <div>
      <Select
        ref={selectRef}
        {...restProps}
        value={canResolveValue ? value : ""}
        onChange={onChange}
        error={error}
        onOpen={determineMenuPosition}
        IconComponent={ExpandMoreIcon}
        sx={({ palette }) => ({
          width: "100%",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? palette.error.main : palette.divider,
            borderRadius: 1,
          },
        })}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight,
              overflowY: "auto",
            },
          },
          anchorOrigin,
          transformOrigin,
        }}
        displayEmpty
        renderValue={
          canResolveValue
            ? undefined
            : () => (
                // 1.4375em comes from the wrapper inside Select, it's needed here for vertical centering
                <Box minHeight="1.4375em" display="flex" alignItems="center">
                  <Typography>
                    {emptyValueText ??
                      t(Resources.Pages.Content.BankAccounts.NoAccountSet)}
                  </Typography>
                </Box>
              )
        }
      >
        {accounts.map((account) => (
          <MenuItem
            key={account.bankAccountID}
            value={account.publicID}
            disabled={account.status !== BankAccountStatus.Active}
          >
            <Box width="100%" display="flex" justifyContent="space-between">
              <Account
                {...account}
                isPrimaryChipHidden={isPrimaryChipHidden}
                balanceVariant={
                  isBalanceVisible && !account.isReceiveOnly
                    ? "visible"
                    : "hidden"
                }
              />
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText error={error} sx={{ mt: 0.5, minHeight: "20px" }}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};
